import type { ChoiceListItem } from "../../FinancialClass/models/financialClass";

export const PREFERENCES_TYPE_GENERAL = 0;
export const PREFERENCES_TYPE_INPATIENT = 1;
export const PREFERENCES_TYPE_OUTPATIENT = 2;

export const mapPreferencesTypeToString = (preferencesType: number) => {
  switch (preferencesType) {
    case PREFERENCES_TYPE_INPATIENT:
      return "InPatientPreferences";
    case PREFERENCES_TYPE_OUTPATIENT:
      return "OutPatientPreferences";
    case PREFERENCES_TYPE_GENERAL:
      return "GeneralPreferences";
    default:
  }

  throw new Error("Unsupported Preferences Type");
};

export const PROFESSIONAL_CODING_OPTIONS = {
  ForAllUsers: "ForAllUsers",
  ForIndividualUsers: "ForIndividualUsers",
  None: "None",
} as const;

export type ProfessionalCodingType =
  (typeof PROFESSIONAL_CODING_OPTIONS)[keyof typeof PROFESSIONAL_CODING_OPTIONS];

export interface GeneralPreferencesObj {
  Version?: string;

  SaveCopyOfInterfacedRecordsToTrucodeDatabase?: boolean;
  ShowAllCodeGridsOnASingleTabByEncounterType?: boolean;
  OpenRecordToCodingTab?: boolean;
  UseSequestrationReduction?: boolean;
  AllProviderSameSpecialtyDetermination?: boolean;
  AllowProfessionalCoding?: ProfessionalCodingType;
  CopyFacilityTargets?: string[];
}

export interface InPatientPreferences {
  Version?: string;

  EnableECodeEdits?: boolean;
  EnableECodePlaceOfOccurrenceEdits?: boolean;
  WarnOfDuplicateCmProcedureCodes?: boolean;
  AllowInvalidModifiers?: boolean;
  AutomaticallyResequence?: boolean;
  ResequenceCptByTotalFacilityRvu?: boolean;
  AllowHcpcsProcedures?: boolean;
  VerifyProviderInChoiceList?: boolean;
  ShowManagedCareFlag?: boolean;
  PromptForNewbornWeight?: boolean;
  PrintMemo?: boolean;

  AutofillPOAForExemptDiagnosisCodes?: boolean;
  ExemptFromHospitalAcquiredConditionLogic?: boolean;
  PrintCustomFieldsOnCodingSummary?: boolean;
  DaysAProcedureDateMayPrecedeAdmitDate?: string;
  MissingPOAErrorLevel?: string;
  PrintMemoOnDRGChangeReport?: boolean;
}

export interface OutPatientPreferences {
  Version?: string;

  EnableECodeEdits?: boolean;
  EnableECodePlaceOfOccurrenceEdits?: boolean;
  WarnOfDuplicateCmProcedureCodes?: boolean;
  AllowInvalidModifiers?: boolean;
  EnableMedicalNecessityFrequencyEdits?: boolean;
  EnableMedicalNecessityArticlePolicies?: boolean;
  AutomaticallyResequence?: boolean;
  ResequenceCptByTotalFacilityRvu?: boolean;
  CalculateDrgs?: boolean;
  AllowIcdProcedures?: boolean;
  VerifyProviderInChoiceList?: boolean;
  PromptForNewbornWeight?: boolean;
  PrintMemo?: boolean;
  PrintMemoOnAPCChangeReport?: boolean;
  PrintCustomFieldsOnCodingSummary?: boolean;
  DefaultBillType?: string;
  DefaultRevenueCode?: string | null;
  AutoFillProviderInChoiceList?: boolean;
  CalculateApcForAllClaimDispositions?: boolean;
  IncludeAllFeeSchedulePricing?: boolean;
}

export interface ProfessionalPreferences {
  Version?: string;

  EnableExternalCauseOfInjuryAndPlaceOfOccurrenceEdits?: boolean;
  EnableActivityAndStatusExternalCauseEdits?: boolean;
  AllowInvalidModifiers?: boolean;
  EnableMedicalNecessityFrequencyEdits?: boolean;
  EnableMedicalNecessityPolicyArticles?: boolean;
  DisplayNonFacilityPracticeExpenseRVU?: boolean;
  DisplayFacilityPracticeExpenseRVU?: boolean;
  DisplayMalpracticeRVU?: boolean;
  VerifyProviderInChoiceList?: boolean;
  AutofillProviderFieldsToMatchAttendingPhysician?: boolean;
  EnableAutomaticCPTCodeResequencingByTotalRVU?: boolean;
}

export interface Preferences {
  inPatient: InPatientPreferences;
  outPatient: OutPatientPreferences;
}

export type AnyPreferencesType =
  | GeneralPreferencesObj
  | InPatientPreferences
  | OutPatientPreferences;

export const MISSING_POA_DDL: ChoiceListItem[] = [
  { id: "Ignore", text: "Ignore" },
  { id: "Warn", text: "Warn" },
  { id: "Error", text: "Error" },
];

export const DEFAULT_ATTESTATION_WORDING_TEXT =
  "I certify that the narrative descriptions of the principal and secondary diagnoses and major procedures performed are accurate and complete to the best of my knowledge.";
